import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SCRAPE_DATA } from '../data/scrapeData';
import { GLOBAL_COMPANY_INFO, getResizedImageUrl } from '../lib/cms';
import ContactForm from '../components/ContactForm';
import GalleryLightbox from '../components/GalleryLightbox';

export default function MalerNorresundbyPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const data = SCRAPE_DATA.norresundby;

  const galleryImages = [
    {
      src: 'https://assets.auxodev.dk/assets/f8dcc12c-2687-46b4-9001-d689bb79e5d8/img-20240807-125724-scaled.jpg',
      alt: 'Mørkeblå trægarage med nymalet facade',
      title: 'Mørkeblå trægarage i Nørresundby'
    },
    {
      src: 'https://assets.auxodev.dk/assets/f8dcc12c-2687-46b4-9001-d689bb79e5d8/img-20240807-125658-scaled.jpg',
      alt: 'Mørkeblå træskur med tagrende',
      title: 'Træskur nymalet udendørs'
    },
    {
      src: 'https://assets.auxodev.dk/assets/f8dcc12c-2687-46b4-9001-d689bb79e5d8/img-20240521-125720-scaled.jpg',
      alt: 'Moderne hvidt hus med stor grøn have',
      title: 'Moderne hvidt hus'
    },
    {
      src: 'https://assets.auxodev.dk/assets/f8dcc12c-2687-46b4-9001-d689bb79e5d8/img-20240521-125705-scaled.jpg',
      alt: 'Moderne etplanshus med stor have og terrasse',
      title: 'Etplanshus med terrasse'
    }
  ];

  return (
    <div className="bg-[#F8FAFC] pt-28 pb-20">
      
      {/* Banner */}
      <section className="bg-[#1D352E] text-white py-16 mb-16 -mt-[10px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4">
            <span className="text-white text-sm font-semibold tracking-wider uppercase">
              Lokal maler
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight">
              {data.mainHeading}
            </h1>
            <p className="text-gray-200 text-lg leading-relaxed">
              {data.subHead}
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-[#65237e] hover:bg-[#521b67] rounded-xl transition-colors shadow-md"
              >
                <span>Få et uforpligtende tilbud</span>
                <i className="fa-solid fa-arrow-right text-xs" />
              </Link>
              <a
                href={`tel:${GLOBAL_COMPANY_INFO.phone_raw}`}
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 rounded-xl transition-colors nowrap-contact"
              >
                <i className="fa-solid fa-phone text-white" />
                <span className="nowrap-contact">{GLOBAL_COMPANY_INFO.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        
        {/* Section 1: Good craftsmanship */}
        <div className="bg-white rounded-3xl p-8 sm:p-12 border border-slate-200 shadow-sm space-y-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1D352E]">
            {data.sectionTitle}
          </h2>

          <div className="flex flex-wrap gap-4 py-2">
            {data.bullets.map((b, idx) => (
              <span key={idx} className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1D352E]/10 text-[#1D352E] text-xs font-semibold">
                <i className="fa-solid fa-check text-[#65237e]" />
                {b}
              </span>
            ))}
          </div>

          <p className="text-gray-700 leading-relaxed text-base">
            {data.paragraph1}
          </p>
          <p className="text-gray-700 leading-relaxed text-base">
            {data.paragraph2}
          </p>
        </div>

        {/* Section 2: Services list */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 bg-white rounded-3xl p-8 sm:p-12 border border-slate-200 shadow-sm space-y-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1D352E]">
              {data.servicesTitle}
            </h2>
            <p className="text-gray-700 leading-relaxed text-base">
              {data.servicesIntro}
            </p>
            <ul className="space-y-3 pt-2">
              {data.servicesItems.map((item, idx) => (
                <li key={idx} className="flex items-start gap-3 text-gray-800 text-sm font-medium">
                  <div className="w-5 h-5 rounded-full bg-[#65237e]/20 text-[#65237e] flex items-center justify-center shrink-0 mt-0.5">
                    <i className="fa-solid fa-check text-xs" />
                  </div>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {galleryImages.slice(0, 4).map((img, idx) => (
              <div
                key={idx}
                onClick={() => setLightboxIndex(idx)}
                className="rounded-2xl overflow-hidden shadow-sm border border-slate-200 group cursor-pointer"
              >
                <img
                  src={getResizedImageUrl(img.src, 500, 80)}
                  alt={img.alt}
                  className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Section 3: Advantages */}
        <div className="bg-[#1D352E] text-white rounded-3xl p-8 sm:p-12 shadow-xl space-y-8">
          <div className="max-w-3xl space-y-3">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">
              {data.advantagesTitle}
            </h2>
            <p className="text-gray-200 text-base">
              {data.advantagesIntro}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
            {data.advantagesItems.map((adv, idx) => {
              const total = data.advantagesItems.length;
              const rem = total % 3;
              let lgColSpan = "lg:col-span-4";
              if (rem === 1 && idx === total - 1) {
                lgColSpan = "lg:col-span-12";
              } else if (rem === 2 && idx >= total - 2) {
                lgColSpan = "lg:col-span-6";
              }

              const remMd = total % 2;
              let mdColSpan = "md:col-span-1";
              if (remMd === 1 && idx === total - 1) {
                mdColSpan = "md:col-span-2";
              }

              return (
                <div key={idx} className={`${mdColSpan} ${lgColSpan} bg-white/10 p-5 rounded-2xl border border-white/10 space-y-2`}>
                  <i className="fa-solid fa-circle-check text-white text-lg" />
                  <p className="text-sm font-medium text-white">{adv}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Section 4: Contact Paragraphs & Form */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6 space-y-6 bg-white rounded-3xl p-8 sm:p-10 border border-slate-200 shadow-sm">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1D352E]">
              {data.contactTitle}
            </h2>
            {data.contactParagraphs.map((p, idx) => (
              <p key={idx} className="text-gray-700 leading-relaxed text-base">
                {p}
              </p>
            ))}
            <div className="pt-2">
              <a
                href={`tel:${GLOBAL_COMPANY_INFO.phone_raw}`}
                className="inline-flex items-center gap-3 px-6 py-3.5 text-sm font-semibold text-white bg-[#1D352E] hover:bg-[#142520] rounded-xl transition-colors nowrap-contact"
              >
                <i className="fa-solid fa-phone text-white" />
                <span className="nowrap-contact">Ring på {GLOBAL_COMPANY_INFO.phone}</span>
              </a>
            </div>
          </div>

          <div className="lg:col-span-6">
            <ContactForm />
          </div>
        </div>

        {/* Section 5: FAQs */}
        <div className="bg-white rounded-3xl p-8 sm:p-12 border border-slate-200 shadow-sm space-y-8">
          <div className="space-y-2">
            <span className="text-[#65237e] text-xs font-semibold uppercase tracking-wider">
              FAQ
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1D352E]">
              Ofte stillede spørgsmål – Maler i Nørresundby
            </h2>
          </div>

          <div className="space-y-4">
            {data.faqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div key={idx} className="border border-gray-200 rounded-2xl overflow-hidden transition-all duration-300">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full p-5 text-left font-bold text-[#1D352E] flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                    aria-expanded={isOpen}
                  >
                    <span className="text-base sm:text-lg">{faq.q}</span>
                    <i className={`fa-solid fa-chevron-down text-sm text-[#65237e] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <div
                    className={`grid transition-all duration-300 ease-in-out ${
                      isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="p-5 pt-0 text-gray-700 text-sm leading-relaxed border-t border-gray-100 bg-gray-50/50">
                        {faq.a}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>

      <GalleryLightbox
        images={galleryImages}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onPrev={() => setLightboxIndex((prev) => (prev > 0 ? prev - 1 : galleryImages.length - 1))}
        onNext={() => setLightboxIndex((prev) => (prev < galleryImages.length - 1 ? prev + 1 : 0))}
      />
    </div>
  );
}
